import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Save, CheckCircle2, FileText, Download, Eye, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

/*
 * TITAN INFRA — online ICAF (Subscriber Application Form).
 * - single scrollable page, 8 sections
 * - auto-save every 3s if dirty and status=draft
 * - live PDF preview via signed URL
 * - electronic signature: canvas + typed confirmation
 * - once submitted (status=submitted), the whole form goes read-only
 */

const COPY = {
  fr: {
    overline: "Souscription",
    title: "Formulaire de souscription (ICAF)",
    lead: "Ce formulaire recueille les éléments nécessaires à votre souscription — identité, résidence fiscale, origine des fonds, profil et signature. Vos réponses sont enregistrées automatiquement.",
    saved: "Brouillon enregistré",
    saving: "Enregistrement…",
    error: "Enregistrement impossible pour l'instant",
    submitted: "Formulaire signé et envoyé",
    submittedBody: "Merci — votre ICAF a été enregistré. Vous le retrouverez dans vos documents.",
    editLocked: "Ce formulaire a été signé et ne peut plus être modifié.",
    preview: "Aperçu PDF",
    submit: "Signer et envoyer",
    submitConfirm: "Confirmer la signature électronique et l'envoi de ce formulaire ?",
    resetSig: "Effacer",
    // section titles
    s1: "Identification du souscripteur",
    s2: "Résidence fiscale",
    s3: "Origine des fonds et du patrimoine",
    s4: "Profil d'investisseur",
    s5: "Déclaration PPE",
    s6: "Sanctions internationales",
    s7: "Mode de contact",
    s8: "Signature",
    // labels
    types: [["individual","Personne physique"],["entity","Personne morale / entité"]],
    lastName: "Nom / Dénomination sociale", firstName: "Prénom",
    birth: "Date de naissance / de constitution", nationality: "Nationalité(s)",
    idNum: "N° pièce d'identité / d'immatriculation", address: "Adresse complète",
    email: "Email", phone: "Téléphone",
    taxCountry: "Pays de résidence fiscale principal", tin: "Numéro d'identification fiscale (TIN)",
    taxRes: [["single","Résident d'un seul pays"],["multiple","Plusieurs résidences fiscales"]],
    fundsIntro: "Origine des fonds (une ou plusieurs)",
    fundsSavings: "Épargne personnelle",
    fundsIncome: "Revenus professionnels",
    fundsSale: "Vente d'actifs (immobilier, entreprise, portefeuille…)",
    fundsInheritance: "Héritage / donation",
    fundsOther: "Autre (préciser)",
    subAmount: "Montant envisagé de la souscription",
    fundsCountry: "Origine géographique des fonds (pays banque)",
    experience: "Expérience avec des investissements à risque",
    exp: [["regular","Oui, régulièrement"],["occasional","Oui, occasionnellement"],["never","Non, jamais"]],
    horizon: "Horizon d'investissement",
    hz: [["<2y","Moins de 2 ans"],["2-5y","2 à 5 ans"],[">5y","Plus de 5 ans"]],
    risk: "Je comprends que ce placement comporte un risque de perte totale ou partielle",
    yn: [["yes","Oui"],["no","Non"]],
    share: "Ce placement représente-t-il une part raisonnable de mon patrimoine ?",
    pep: "Suis-je (ou un proche) une personne politiquement exposée ?",
    pepDetails: "Si oui, préciser la fonction et le pays",
    sanctions: "Je confirme n'être visé par aucune sanction internationale, ne pas résider dans un territoire sous sanctions, et que les fonds ne proviennent d'aucune activité illicite.",
    channelSource: "Comment avez-vous pris connaissance du projet Titan Infra ?",
    channelIntro: "Nom de la personne qui vous a mis en relation (le cas échéant)",
    channelPrivate: "Je confirme ne pas avoir eu connaissance de cette opportunité via une communication publique (publicité, réseau social ouvert, site web indexé).",
    stmt: "Je soussigné(e) certifie que les informations fournies sont exactes, complètes et sincères. Je m'engage à informer Titan Infra sans délai de toute modification substantielle.",
    place: "Fait à", date: "Le (date)",
    typed: "Mention manuscrite (à taper) : « Lu et approuvé — Prénom Nom »",
    drawSig: "Signature manuscrite (dessinez ci-dessous)",
  },
  en: {
    overline: "Subscription",
    title: "Subscriber Application Form (ICAF)",
    lead: "This form collects the elements necessary for your subscription — identity, tax residence, source of funds, profile and signature. Your answers are saved automatically.",
    saved: "Draft saved", saving: "Saving…", error: "Cannot save right now",
    submitted: "Form signed and submitted",
    submittedBody: "Thank you — your ICAF has been recorded. You'll find it in your documents.",
    editLocked: "This form has been signed and can no longer be edited.",
    preview: "PDF preview",
    submit: "Sign and submit",
    submitConfirm: "Confirm the electronic signature and submission of this form?",
    resetSig: "Clear",
    s1:"Subscriber identification", s2:"Tax residence", s3:"Source of funds & wealth",
    s4:"Investor profile", s5:"PEP declaration", s6:"International sanctions",
    s7:"Introduction channel", s8:"Signature",
    types: [["individual","Individual"],["entity","Legal entity"]],
    lastName:"Last name / Corporate name", firstName:"First name",
    birth:"Date of birth / of incorporation", nationality:"Nationality(ies)",
    idNum:"ID / registration number", address:"Full address",
    email:"Email", phone:"Phone",
    taxCountry:"Principal country of tax residence", tin:"Tax identification number (TIN)",
    taxRes:[["single","Resident of a single country"],["multiple","Multiple tax residences"]],
    fundsIntro:"Source of funds (one or several)",
    fundsSavings:"Personal savings", fundsIncome:"Professional income",
    fundsSale:"Sale of assets (real estate, business, portfolio…)",
    fundsInheritance:"Inheritance / donation", fundsOther:"Other (specify)",
    subAmount:"Envisaged subscription amount",
    fundsCountry:"Geographic origin of the funds (bank country)",
    experience:"Experience with risk-bearing investments",
    exp:[["regular","Yes, regularly"],["occasional","Yes, occasionally"],["never","No, never"]],
    horizon:"Investment horizon",
    hz:[["<2y","Less than 2 years"],["2-5y","2 to 5 years"],[">5y","More than 5 years"]],
    risk:"I understand this investment involves a risk of total or partial loss.",
    yn:[["yes","Yes"],["no","No"]],
    share:"Does this investment represent a reasonable share of my overall wealth?",
    pep:"Am I (or a close relative) a politically exposed person?",
    pepDetails:"If yes, specify the function and country",
    sanctions:"I confirm not to be subject to any international sanctions, not to reside in any territory under sanctions, and that the funds do not originate from any unlawful activity.",
    channelSource:"How did you become aware of the Titan Infra project?",
    channelIntro:"Name of the person who introduced you (if any)",
    channelPrivate:"I confirm I did not become aware of this opportunity through public communication (advertising, open social network, indexed website).",
    stmt:"The undersigned certifies that the information provided is accurate, complete and truthful. The undersigned undertakes to inform Titan Infra without delay of any substantial change.",
    place:"Executed at", date:"Date",
    typed:"Handwritten mention (type it): \"Read and approved — First Last\"",
    drawSig:"Handwritten signature (draw below)",
  },
};

const empty = {
  lang: "fr",
  subscriber_type: "individual",
  last_name: "", first_name: "", birth_or_incorp_date: "", nationality: "",
  id_number: "", address: "", email: "", phone: "",
  tax_country: "", tin: "", tax_residency: "single",
  funds_savings: false, funds_income: false, funds_asset_sale: false, funds_inheritance: false, funds_other: "",
  subscription_amount: "", funds_country: "",
  experience: "", horizon: "", risk_understood: "", reasonable_share: "",
  pep: "no", pep_details: "",
  sanctions_confirmed: false,
  channel_source: "", channel_introducer: "", channel_private_confirmed: false,
  place: "", date: new Date().toISOString().slice(0,10),
  signature_typed: "", signature_svg: "",
};

export default function Subscription() {
  const { api, token, user } = useAuth();
  const { lang } = useI18n();
  const c = COPY[lang === "en" ? "en" : "fr"];
  const H = { Authorization: `Bearer ${token}` };

  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("draft");
  const [submittedAt, setSubmittedAt] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const sigRef = useRef(null);
  const dirtyRef = useRef(false);
  const initialisedRef = useRef(false);

  const locked = status === "submitted";

  // Load existing submission
  useEffect(() => {
    axios.get(`${api}/me/submission`, { headers: H }).then(r => {
      const d = r.data.data || {};
      setStatus(r.data.status);
      setSubmittedAt(r.data.submitted_at);
      setForm(f => ({
        ...f, ...d,
        email: d.email || user?.email || "",
        last_name: d.last_name || user?.full_name || "",
        lang: d.lang || (lang === "en" ? "en" : "fr"),
      }));
      initialisedRef.current = true;
    }).catch(() => { initialisedRef.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => {
    if (locked) return;
    dirtyRef.current = true;
    setForm(f => ({ ...f, ...patch }));
  };

  // Debounced auto-save
  useEffect(() => {
    if (!initialisedRef.current || locked) return;
    if (!dirtyRef.current) return;
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        // capture current signature drawing
        let sig = form.signature_svg;
        if (sigRef.current && !sigRef.current.isEmpty()) sig = sigRef.current.toDataURL("image/png");
        const payload = { ...form, signature_svg: sig };
        await axios.put(`${api}/me/submission`, { data: payload }, { headers: H });
        setSaveState("saved"); dirtyRef.current = false;
        setTimeout(() => setSaveState("idle"), 1200);
      } catch { setSaveState("error"); }
    }, 1200);
    return () => clearTimeout(t);
  }, [form, locked, api, token]); // eslint-disable-line

  const refreshPreview = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/me/submission/preview.pdf`, {
        headers: H, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url); setShowPreview(true);
    } catch { toast.error("Preview failed"); }
  }, [api, token]); // eslint-disable-line

  const submit = async () => {
    if (!window.confirm(c.submitConfirm)) return;
    let sig = form.signature_svg;
    if (sigRef.current && !sigRef.current.isEmpty()) sig = sigRef.current.toDataURL("image/png");
    const payload = { ...form, signature_svg: sig };
    try {
      const r = await axios.post(`${api}/me/submission/submit`,
        { data: payload }, { headers: H });
      setStatus("submitted");
      setSubmittedAt(r.data.submitted_at);
      setForm(f => ({ ...f, signature_svg: sig })); // mirror server state so locked view renders the image without reload
      toast.success(c.submitted);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  const canSubmit = useMemo(() => {
    const hasDrawnSig = !!form.signature_svg || (sigRef.current && !sigRef.current.isEmpty());
    return !locked &&
      form.last_name && form.email && form.subscription_amount &&
      form.signature_typed && form.place && form.date &&
      form.risk_understood === "yes" &&
      form.sanctions_confirmed && form.channel_private_confirmed &&
      hasDrawnSig;
  }, [form, locked]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-muted-foreground">{c.overline}</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">{c.title}</h1>
          <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">{c.lead}</p>
        </div>
        <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase">
          <SaveIndicator state={saveState} c={c} />
        </div>
      </div>

      {locked && (
        <div data-testid="locked-banner" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-display text-[16px]">{c.submitted}</p>
            <p className="text-[13px] text-muted-foreground mt-1">{c.submittedBody}{submittedAt ? ` (${new Date(submittedAt).toLocaleString()})` : ""}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="mt-10 space-y-10">
        <Section num="1" title={c.s1} locked={locked}>
          <RadioGroup name="subscriber_type" value={form.subscriber_type} options={c.types} onChange={v => set({ subscriber_type: v })} disabled={locked} />
          <Grid2>
            <Field label={c.lastName} value={form.last_name} onChange={v => set({ last_name: v })} disabled={locked} />
            <Field label={c.firstName} value={form.first_name} onChange={v => set({ first_name: v })} disabled={locked} />
          </Grid2>
          <Grid2>
            <Field label={c.birth} value={form.birth_or_incorp_date} onChange={v => set({ birth_or_incorp_date: v })} disabled={locked} />
            <Field label={c.nationality} value={form.nationality} onChange={v => set({ nationality: v })} disabled={locked} />
          </Grid2>
          <Grid2>
            <Field label={c.idNum} value={form.id_number} onChange={v => set({ id_number: v })} disabled={locked} />
            <Field label={c.phone} value={form.phone} onChange={v => set({ phone: v })} disabled={locked} />
          </Grid2>
          <Field label={c.address} value={form.address} onChange={v => set({ address: v })} multiline disabled={locked} />
          <Field label={c.email} value={form.email} onChange={v => set({ email: v })} type="email" disabled={locked} />
        </Section>

        <Section num="2" title={c.s2} locked={locked}>
          <Grid2>
            <Field label={c.taxCountry} value={form.tax_country} onChange={v => set({ tax_country: v })} disabled={locked} />
            <Field label={c.tin} value={form.tin} onChange={v => set({ tin: v })} disabled={locked} />
          </Grid2>
          <RadioGroup name="tax_residency" value={form.tax_residency} options={c.taxRes} onChange={v => set({ tax_residency: v })} disabled={locked} />
        </Section>

        <Section num="3" title={c.s3} locked={locked}>
          <p className="text-[13px] text-muted-foreground mb-2">{c.fundsIntro}</p>
          <Check label={c.fundsSavings} checked={form.funds_savings} onChange={v => set({ funds_savings: v })} disabled={locked} testId="cbx-savings" />
          <Check label={c.fundsIncome} checked={form.funds_income} onChange={v => set({ funds_income: v })} disabled={locked} testId="cbx-income" />
          <Check label={c.fundsSale} checked={form.funds_asset_sale} onChange={v => set({ funds_asset_sale: v })} disabled={locked} testId="cbx-sale" />
          <Check label={c.fundsInheritance} checked={form.funds_inheritance} onChange={v => set({ funds_inheritance: v })} disabled={locked} testId="cbx-inheritance" />
          <Field label={c.fundsOther} value={form.funds_other} onChange={v => set({ funds_other: v })} disabled={locked} />
          <Grid2>
            <Field label={c.subAmount} value={form.subscription_amount} onChange={v => set({ subscription_amount: v })} disabled={locked} testId="sub-amount" />
            <Field label={c.fundsCountry} value={form.funds_country} onChange={v => set({ funds_country: v })} disabled={locked} />
          </Grid2>
        </Section>

        <Section num="4" title={c.s4} locked={locked}>
          <QLabel>{c.experience}</QLabel>
          <RadioGroup name="experience" value={form.experience} options={c.exp} onChange={v => set({ experience: v })} disabled={locked} />
          <QLabel>{c.horizon}</QLabel>
          <RadioGroup name="horizon" value={form.horizon} options={c.hz} onChange={v => set({ horizon: v })} disabled={locked} />
          <QLabel>{c.risk}</QLabel>
          <RadioGroup name="risk_understood" value={form.risk_understood} options={c.yn} onChange={v => set({ risk_understood: v })} disabled={locked} />
          <QLabel>{c.share}</QLabel>
          <RadioGroup name="reasonable_share" value={form.reasonable_share} options={c.yn} onChange={v => set({ reasonable_share: v })} disabled={locked} />
        </Section>

        <Section num="5" title={c.s5} locked={locked}>
          <QLabel>{c.pep}</QLabel>
          <RadioGroup name="pep" value={form.pep} options={c.yn} onChange={v => set({ pep: v })} disabled={locked} />
          {form.pep === "yes" && (
            <Field label={c.pepDetails} value={form.pep_details} onChange={v => set({ pep_details: v })} disabled={locked} />
          )}
        </Section>

        <Section num="6" title={c.s6} locked={locked}>
          <Check label={c.sanctions} checked={form.sanctions_confirmed} onChange={v => set({ sanctions_confirmed: v })} disabled={locked} testId="cbx-sanctions" />
        </Section>

        <Section num="7" title={c.s7} locked={locked}>
          <Field label={c.channelSource} value={form.channel_source} onChange={v => set({ channel_source: v })} disabled={locked} multiline />
          <Field label={c.channelIntro} value={form.channel_introducer} onChange={v => set({ channel_introducer: v })} disabled={locked} />
          <Check label={c.channelPrivate} checked={form.channel_private_confirmed} onChange={v => set({ channel_private_confirmed: v })} disabled={locked} testId="cbx-private" />
        </Section>

        <Section num="8" title={c.s8} locked={locked}>
          <p className="text-[13px] text-muted-foreground leading-relaxed">{c.stmt}</p>
          <Grid2>
            <Field label={c.place} value={form.place} onChange={v => set({ place: v })} disabled={locked} testId="sig-place" />
            <Field label={c.date} value={form.date} onChange={v => set({ date: v })} disabled={locked} type="date" testId="sig-date" />
          </Grid2>
          <Field label={c.typed} value={form.signature_typed} onChange={v => set({ signature_typed: v })} disabled={locked} testId="sig-typed" />

          {!locked && (
            <div className="mt-4">
              <p className="overline text-muted-foreground mb-2">{c.drawSig}</p>
              <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="sig-canvas-wrap">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#0B2648"
                  canvasProps={{ className: "block w-full h-40", "data-testid": "sig-canvas" }}
                  onEnd={() => { dirtyRef.current = true; setForm(f => ({ ...f })); }}
                />
              </div>
              <button type="button" data-testid="sig-clear" onClick={() => { sigRef.current?.clear(); set({ signature_svg: "" }); }}
                className="mt-2 text-[11px] tracking-widest uppercase text-muted-foreground hover:text-foreground">
                <RefreshCw className="inline w-3 h-3 mr-1" /> {c.resetSig}
              </button>
            </div>
          )}
          {locked && form.signature_svg && (
            <div className="mt-4">
              <p className="overline text-muted-foreground mb-2">{c.drawSig}</p>
              <img src={form.signature_svg} alt="Signature" data-testid="sig-image-locked" className="border border-border rounded-lg bg-white max-w-xs" />
            </div>
          )}
        </Section>
      </div>

      {/* Action bar */}
      <div className="mt-14 flex flex-wrap gap-3 justify-between items-center">
        <button onClick={refreshPreview} data-testid="preview-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border hover:bg-secondary text-[12px] tracking-widest uppercase">
          <Eye className="w-4 h-4" /> {c.preview}
        </button>
        {!locked && (
          <button onClick={submit} disabled={!canSubmit} data-testid="submit-btn"
            className={`inline-flex items-center gap-2 h-11 px-6 rounded-full text-[12px] tracking-widest uppercase transition ${
              canSubmit ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-secondary text-muted-foreground cursor-not-allowed"
            }`}>
            <CheckCircle2 className="w-4 h-4" /> {c.submit}
          </button>
        )}
      </div>

      {/* PDF preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setShowPreview(false)}>
          <div className="relative w-full max-w-4xl h-[90vh] bg-card rounded-2xl overflow-hidden border border-border" onClick={e => e.stopPropagation()}>
            <div className="h-12 flex items-center justify-between px-4 border-b border-border">
              <p className="text-[12px] tracking-widest uppercase text-muted-foreground">{c.preview}</p>
              <button onClick={() => setShowPreview(false)} data-testid="preview-close" className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <iframe src={pdfUrl} title="ICAF preview" className="w-full h-[calc(90vh-3rem)] bg-white" data-testid="preview-iframe" />
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── UI atoms ────────────────────────────────────────────────────────────────
const SaveIndicator = ({ state, c }) => {
  const map = {
    idle:   { label: "", color: "text-muted-foreground", icon: null },
    saving: { label: c.saving, color: "text-amber-400", icon: <Save className="w-3 h-3" /> },
    saved:  { label: c.saved,  color: "text-emerald-400", icon: <CheckCircle2 className="w-3 h-3" /> },
    error:  { label: c.error,  color: "text-red-400", icon: <AlertTriangle className="w-3 h-3" /> },
  }[state] || { label: "", color: "text-muted-foreground" };
  if (!map.label) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${map.color}`} data-testid="save-state">
      {map.icon} {map.label}
    </span>
  );
};

const Section = ({ num, title, children }) => (
  <section className="rounded-2xl border border-border bg-card p-6 md:p-8" data-testid={`section-${num}`}>
    <p className="mb-6">
      <span className="text-[10px] tracking-widest uppercase font-semibold text-emerald-400">Section {num}</span>
      <span className="mx-3 text-border">·</span>
      <span className="font-display text-xl text-foreground">{title}</span>
    </p>
    <div className="space-y-4">{children}</div>
  </section>
);

const QLabel = ({ children }) => <p className="text-[13px] text-foreground mt-2">{children}</p>;

const Grid2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const Field = ({ label, value, onChange, disabled, type = "text", multiline = false, testId }) => (
  <label className="block">
    <span className="overline text-muted-foreground block mb-1.5">{label}</span>
    {multiline ? (
      <textarea disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="w-full min-h-[64px] bg-transparent border border-border px-3 py-2 text-[13px] disabled:opacity-60 focus:border-foreground outline-none transition-colors" />
    ) : (
      <input disabled={disabled} type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-10 bg-transparent border border-border px-3 text-[13px] disabled:opacity-60 focus:border-foreground outline-none transition-colors" />
    )}
  </label>
);

const Check = ({ label, checked, onChange, disabled, testId }) => (
  <label className={`flex items-start gap-3 py-1 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={e => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-1 w-4 h-4 accent-emerald-500" />
    <span className="text-[13px] leading-relaxed">{label}</span>
  </label>
);

const RadioGroup = ({ name, value, options, onChange, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([val, lab]) => (
      <button
        type="button" key={val} disabled={disabled}
        onClick={() => onChange(val)}
        data-testid={`radio-${name}-${val}`}
        className={`px-4 h-10 rounded-full border text-[12px] tracking-wide transition ${
          value === val
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "bg-transparent border-border text-muted-foreground hover:text-foreground"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >{lab}</button>
    ))}
  </div>
);
